import { logger } from "@/lib/logger";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Activity, CheckCircle2, XCircle, TrendingUp, Download, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface OMOPPatientStatus {
  profile_id: string;
  first_name: string;
  last_name: string;
  is_mapped: boolean;
  omop_person_id: number | null;
}

export default function SiteOMOPDashboard() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();

  // Fetch OMOP mapping details for this site
  const { data: omopData, isLoading, refetch } = useQuery({
    queryKey: ['site-omop-details', siteId],
    queryFn: async () => {
      // Get all patients assigned to this site with their OMOP mapping status
      const { data: assignments, error: assignError } = await supabase
        .from('patient_trial_site_assignments')
        .select(`
          profile_id,
          profiles!profile_id (
            first_name,
            last_name
          )
        `)
        .eq('trial_site_id', siteId!)
        .limit(100);

      if (assignError) {
        logger.error('Error fetching assignments:', assignError);
        return { patients: [], totalCount: 0, mappedCount: 0 };
      }

      const profileIds = assignments?.map(a => a.profile_id) || [];
      
      if (profileIds.length === 0) {
        return { patients: [], totalCount: 0, mappedCount: 0 };
      }

      // Get OMOP mappings for these patients
      const { data: omopMappings, error: omopError } = await supabase
        .from('omop_person_id_map')
        .select('profile_id, omop_person_id')
        .in('profile_id', profileIds);

      if (omopError) {
        logger.warn('Error fetching OMOP mappings:', omopError);
      }

      const mappingMap = new Map(
        (omopMappings || []).map(m => [m.profile_id, m.omop_person_id])
      );

      const patients: OMOPPatientStatus[] = assignments?.map(a => ({
        profile_id: a.profile_id,
        first_name: (a.profiles as { first_name: string; last_name: string })?.first_name || 'Unknown',
        last_name: (a.profiles as { first_name: string; last_name: string })?.last_name || '',
        is_mapped: mappingMap.has(a.profile_id),
        omop_person_id: mappingMap.get(a.profile_id) || null,
      })) || [];

      return {
        patients,
        totalCount: patients.length,
        mappedCount: patients.filter(p => p.is_mapped).length,
      };
    },
    enabled: !!siteId,
  });

  const mappingRate = omopData?.totalCount 
    ? ((omopData.mappedCount / omopData.totalCount) * 100).toFixed(1)
    : '0.0';

  if (!siteId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <p className="text-center">Site ID is required</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title="OMOP Mapping Dashboard"
        description="View and manage OMOP CDM mappings for site patients"
        url={`/trial-site/${siteId}/omop`}
      />

      <div className="container mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(`/trial-site/${siteId}/patient-management`)} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Patient Management
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="h-8 w-8 text-teal-500" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">OMOP Mapping Dashboard</h1>
                <p className="text-muted-foreground">
                  OMOP Common Data Model mapping status for your patients
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export OMOP CDM
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Patients</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-3xl font-bold">{omopData?.totalCount || 0}</p>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">OMOP Mapped</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-bold text-green-600">{omopData?.mappedCount || 0}</p>
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Mapping Rate</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-full" />
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-3xl font-bold">{mappingRate}%</p>
                    <TrendingUp className="h-5 w-5 text-teal-500" />
                  </div>
                  <Progress value={parseFloat(mappingRate)} className="h-2" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Patient List */}
        <Card>
          <CardHeader>
            <CardTitle>Patient OMOP Status</CardTitle>
            <CardDescription>
              Showing first 100 patients. OMOP mappings enable research interoperability.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient Name</TableHead>
                    <TableHead>OMOP Status</TableHead>
                    <TableHead>OMOP Person ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {omopData?.patients.map((patient) => (
                    <TableRow key={patient.profile_id}>
                      <TableCell className="font-medium">
                        {patient.first_name} {patient.last_name}
                      </TableCell>
                      <TableCell>
                        {patient.is_mapped ? (
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            <CheckCircle2 className="mr-1 h-3 w-3" />
                            Mapped
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                            <XCircle className="mr-1 h-3 w-3" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {patient.omop_person_id || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!omopData?.patients || omopData.patients.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                        No patients found for this site
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
